/**
 * CategoryListPage — admin categories TREE (P1 PIM Wave Task 5, design notes
 * Surface 3). Replaces the flat `AdminTable` with the `_shared/CategoryTree`
 * kit primitive: indent + expand/collapse, sibling dnd reorder (per-category
 * `sort_order` PATCH — no batch reorder route, recon C5), and a client-side
 * search that auto-expands ancestors of matches (there is no admin tree
 * endpoint nor a `q` param on the list route — recon C4 — so the whole set
 * is drained via `useAllAdminCategories()` and nested/filtered here).
 *
 * Row anatomy (brief §4): drag handle + chevron (owned by `CategoryTree`)
 * then this page's `renderRow` — name in the current admin language (RU
 * fallback) + slug + archived/photo-rules badges + Edit / Add-child /
 * Archive|Unarchive / "Manage fields →" (the last is a dead link until
 * Task 11 registers `/admin/categories/:id/fields` — expected).
 *
 * PIM Wave P1 Task 6: wires Archive/Unarchive. Archive opens a confirm
 * `BottomSheet` (USER RULING C1=1a, binding — no product count in the
 * copy: no backend source exists for one, see `p1-task6-brief.md`; the
 * copy instead states the real semantics — archived category drops out of
 * the public tree, its products stay browsable/active, descendants keep
 * their own archived flag, unarchive exists). Unarchive is immediate (no
 * confirm), mirroring the `FieldLibraryPage` archive/unarchive idiom.
 * Aliases and photo_rules editing (both live inside `CategoryFormDialog`)
 * are the remaining P1 category lifecycle work.
 *
 * PIM Wave P1 Task 7: wires "Merge into…" via `CategoryMergeDialog`
 * (pick/confirm/result steps — USER RULING C2, see that file's header).
 * The action is NOT gated on `is_archived` — verified in
 * `marketplace/service.py`'s `merge_category` docstring: the SOURCE may
 * already be archived (only the TARGET is guarded against being archived),
 * so an archived category can still be merged away as a legal cleanup path.
 * The prior soft-delete affordance (which lived awkwardly
 * inside the edit-dialog flow) is DROPPED from this rewrite: the brief's row
 * anatomy list only names Edit/Add child/Manage-fields(/Archive), and the
 * design direction moves to archive/unarchive as the reversible lifecycle
 * action instead of hard delete. `deleteCategory`/`useDeleteCategory` are
 * left intact in `api.ts`/`hooks.ts` in case a later surface still wants
 * them — nothing is removed there, only unwired from this page.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Archive, ArchiveRestore, FolderPlus, GitMerge, Pencil } from "lucide-react";

import { BottomSheet, Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import {
  AdminPageHeader,
  AdminStates,
  CategoryTree,
  type CategoryTreeRenderMeta,
} from "@/features/admin/_shared";
import {
  useAllAdminCategories,
  useArchiveCategory,
  usePatchCategory,
  useUnarchiveCategory,
} from "@/features/admin/categories/hooks";
import { CategoryFormDialog } from "@/features/admin/categories/CategoryFormDialog";
import { CategoryMergeDialog } from "@/features/admin/categories/components/CategoryMergeDialog";
import { PendingChangesBanner } from "@/features/admin/changesets/components/PendingChangesBanner";
import type { AdminCategory } from "@/features/admin/categories/api";

const EMPTY_CATEGORIES: AdminCategory[] = [];

const ICON_BUTTON_CLASS =
  "grid h-9 w-9 shrink-0 place-items-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]";

/** Mirrors the backend tree ORDER BY (`marketplace/repository.py:127`,
 * `CATEGORY_TREE_CTE_SQL`: `ORDER BY depth, sort_order, name_ru`). Within a
 * sibling group `depth` is constant, so the effective comparator is
 * `(sort_order, name_ru)`. */
function compareSiblings(a: AdminCategory, b: AdminCategory): number {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.name_ru.localeCompare(b.name_ru, "ru");
}

/** Current-admin-language label with RU fallback (brief §4). */
function categoryLabel(node: AdminCategory, lang: string): string {
  if (lang === "ky" && node.name_ky) return node.name_ky;
  if (lang === "en" && node.name_en) return node.name_en;
  return node.name_ru;
}

/** Walks `parent_id` up to the root, returning ancestor ids (nearest first). */
function ancestorIdsOf(id: string, byId: Map<string, AdminCategory>): string[] {
  const ids: string[] = [];
  let current = byId.get(id);
  while (current && current.parent_id) {
    ids.push(current.parent_id);
    current = byId.get(current.parent_id);
  }
  return ids;
}

type DialogState =
  | { open: false }
  | { open: true; category: AdminCategory | null; parentId: string | null };

/** `CategoryTree` is generic over `{id, parentId}` (camelCase, domain-agnostic
 * — Task 7's merge picker will reuse it for something that isn't a
 * category). The wire DTO is snake_case (`parent_id`), so this adds the
 * camelCase alias the primitive requires without touching `AdminCategory`
 * itself. */
type CategoryNode = AdminCategory & { parentId: string | null };

/** The in-flight sibling group's pinned optimistic order (OptionsEditor
 * precedent) — applied by overriding those nodes' `sort_order` so the
 * existing comparator renders the dragged order immediately, before the
 * PATCH round-trip / invalidation lands. */
interface OptimisticOrder {
  orderedIds: string[];
}

export function CategoryListPage(): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);
  const lang = i18n.language;

  const { data, isLoading, isError, refetch } = useAllAdminCategories();
  const all = data ?? EMPTY_CATEGORIES;
  const byId = useMemo(() => new Map(all.map((c) => [c.id, c])), [all]);

  // --- Expansion state: default = roots expanded, rest collapsed. Seeded
  // ONCE when data first arrives so a background refetch never clobbers a
  // user's manual toggles (brief §5). ---
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const expansionSeededRef = useRef(false);
  useEffect(() => {
    if (expansionSeededRef.current || all.length === 0) return;
    setExpandedIds(new Set(all.filter((c) => c.parent_id === null).map((c) => c.id)));
    expansionSeededRef.current = true;
  }, [all]);

  const toggleExpanded = useCallback((id: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // --- Search (client-side — no `q` param on the list route, brief §6).
  // Entering/leaving search stashes/restores the pre-search expansion set. ---
  const [search, setSearch] = useState("");
  const preSearchExpandedRef = useRef<Set<string> | null>(null);
  const handleSearchChange = (next: string): void => {
    const wasEmpty = search.trim() === "";
    const willBeEmpty = next.trim() === "";
    if (wasEmpty && !willBeEmpty) {
      preSearchExpandedRef.current = expandedIds;
    } else if (!wasEmpty && willBeEmpty && preSearchExpandedRef.current) {
      setExpandedIds(preSearchExpandedRef.current);
      preSearchExpandedRef.current = null;
    }
    setSearch(next);
  };

  const q = search.trim().toLowerCase();
  const isSearching = q !== "";

  const matchIds = useMemo(() => {
    if (!isSearching) return null;
    const set = new Set<string>();
    for (const c of all) {
      if (
        c.name_ru.toLowerCase().includes(q) ||
        (c.name_ky ?? "").toLowerCase().includes(q) ||
        (c.name_en ?? "").toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q)
      ) {
        set.add(c.id);
      }
    }
    return set;
  }, [all, q, isSearching]);

  // Matches + their ancestors only; non-matching siblings are hidden.
  const visibleNodes = useMemo(() => {
    if (!isSearching || !matchIds) return all;
    const visible = new Set<string>();
    for (const id of matchIds) {
      visible.add(id);
      for (const ancestorId of ancestorIdsOf(id, byId)) visible.add(ancestorId);
    }
    return all.filter((c) => visible.has(c.id));
  }, [all, isSearching, matchIds, byId]);

  // Ancestors of a match are force-expanded while searching (brief §6);
  // union'd with the live `expandedIds` so a user's own toggles still stick.
  const effectiveExpandedIds = useMemo(() => {
    if (!isSearching || !matchIds) return expandedIds;
    const merged = new Set(expandedIds);
    for (const id of matchIds) {
      for (const ancestorId of ancestorIdsOf(id, byId)) merged.add(ancestorId);
    }
    return merged;
  }, [isSearching, matchIds, byId, expandedIds]);

  // --- Sibling dnd reorder — mirrors OptionsEditor.tsx's handleDragEnd:
  // reindex 0..N-1 within the dragged sibling group, PATCH only the
  // categories whose sort_order actually changed, pin the dragged order
  // optimistically through the Promise.all. On failure (final-review
  // Finding D): only the OPTIMISTIC UI reverts (`optimisticOrder` cleared +
  // `refetch()` re-renders from server truth) + an error toast fires — any
  // PATCH(es) that already committed before the rejection stay persisted
  // server-side (`Promise.all` doesn't undo settled requests). That can
  // leave two siblings sharing a `sort_order` until the next successful
  // reorder overwrites it (self-heals; the ordering is merely unstable in
  // the meantime, never corrupted). ---
  const patchMutation = usePatchCategory();
  const [optimisticOrder, setOptimisticOrder] = useState<OptimisticOrder | null>(null);
  const [reorderPending, setReorderPending] = useState(false);

  const handleReorderSiblings = async (
    _parentId: string | null,
    orderedIds: string[],
  ): Promise<void> => {
    setOptimisticOrder({ orderedIds });
    setReorderPending(true);
    const changed = orderedIds
      .map((id, index) => ({ category: byId.get(id), nextSortOrder: index }))
      .filter(
        (entry): entry is { category: AdminCategory; nextSortOrder: number } =>
          entry.category !== undefined && entry.category.sort_order !== entry.nextSortOrder,
      );
    try {
      await Promise.all(
        changed.map(({ category, nextSortOrder }) =>
          patchMutation.mutateAsync({ id: category.id, body: { sort_order: nextSortOrder } }),
        ),
      );
    } catch {
      toast.error(t("categories.reorderError"));
      await refetch();
    } finally {
      setOptimisticOrder(null);
      setReorderPending(false);
    }
  };

  // Applies the pinned optimistic order (if any) by overriding sort_order on
  // just the affected nodes — `compareSiblings` then renders it for free.
  const displayNodes = useMemo(() => {
    if (!optimisticOrder) return visibleNodes;
    const orderIndex = new Map(optimisticOrder.orderedIds.map((id, index) => [id, index]));
    return visibleNodes.map((c) =>
      orderIndex.has(c.id) ? { ...c, sort_order: orderIndex.get(c.id)! } : c,
    );
  }, [visibleNodes, optimisticOrder]);

  const treeNodes: CategoryNode[] = useMemo(
    () => displayNodes.map((c) => ({ ...c, parentId: c.parent_id })),
    [displayNodes],
  );

  // --- Create / edit / add-child dialog ---
  const [dialog, setDialog] = useState<DialogState>({ open: false });
  const closeDialog = (): void => setDialog({ open: false });

  // --- Archive / unarchive (P1 Task 6). Unarchive is immediate; archive
  // opens a confirm sheet (USER RULING C1=1a — no product count in the
  // copy, see file header). ---
  const archiveMutation = useArchiveCategory();
  const unarchiveMutation = useUnarchiveCategory();
  const [archiveTarget, setArchiveTarget] = useState<AdminCategory | null>(null);

  const handleUnarchive = (node: AdminCategory): void => {
    unarchiveMutation.mutate(node.id, {
      onSuccess: () => toast.success(t("categories.archive.unarchiveSuccess")),
      onError: () => toast.error(t("error.generic")),
    });
  };

  const confirmArchive = (): void => {
    if (!archiveTarget) return;
    archiveMutation.mutate(archiveTarget.id, {
      onSuccess: () => {
        toast.success(t("categories.archive.archiveSuccess"));
        setArchiveTarget(null);
      },
      onError: () => toast.error(t("error.generic")),
    });
  };

  // --- Merge (P1 Task 7). Not gated on `is_archived` — the SOURCE may
  // already be archived (see file header / CategoryMergeDialog header). ---
  const [mergeSource, setMergeSource] = useState<AdminCategory | null>(null);

  const renderRow = (node: CategoryNode, _meta: CategoryTreeRenderMeta): ReactNode => {
    const label = categoryLabel(node, lang);
    const dimmed = node.is_archived;
    return (
      <div className="flex flex-1 flex-wrap items-center justify-between gap-2 py-1">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className={["truncate font-medium", dimmed ? "opacity-60" : ""].join(" ")}>
              {label}
            </span>
            {node.is_archived ? (
              <GPill
                tone="neutral"
                size="sm"
                leading={<Archive size={12} strokeWidth={1.75} aria-hidden />}
              >
                {t("categories.badges.archived")}
              </GPill>
            ) : null}
            {node.photo_rules ? (
              <GPill tone="neutral" size="sm" leading="camera">
                {t("categories.badges.photoRules")}
              </GPill>
            ) : null}
          </div>
          <span
            className={[
              "truncate font-mono text-xs text-[var(--g-secondary)]",
              dimmed ? "opacity-60" : "",
            ].join(" ")}
          >
            {node.slug}
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <Link
            to={`/admin/categories/${node.id}/fields`}
            data-testid={`admin-categories-manage-fields-${node.id}`}
            className="rounded-[var(--g-radius-sm)] px-2 py-1.5 text-sm font-medium text-[var(--g-primary)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
          >
            {t("categories.actions.manageFields")}
          </Link>
          <button
            type="button"
            data-testid={`admin-categories-edit-${node.id}`}
            aria-label={t("categories.actions.edit", { name: label })}
            onClick={() => setDialog({ open: true, category: node, parentId: null })}
            className={ICON_BUTTON_CLASS}
          >
            <Pencil size={16} strokeWidth={1.75} aria-hidden />
          </button>
          <button
            type="button"
            data-testid={`admin-categories-add-child-${node.id}`}
            aria-label={t("categories.actions.addChild", { name: label })}
            onClick={() => setDialog({ open: true, category: null, parentId: node.id })}
            className={ICON_BUTTON_CLASS}
          >
            <FolderPlus size={16} strokeWidth={1.75} aria-hidden />
          </button>
          {node.is_archived ? (
            <button
              type="button"
              data-testid={`admin-categories-unarchive-${node.id}`}
              aria-label={t("categories.actions.unarchive", { name: label })}
              onClick={() => handleUnarchive(node)}
              className={ICON_BUTTON_CLASS}
            >
              <ArchiveRestore size={16} strokeWidth={1.75} aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              data-testid={`admin-categories-archive-${node.id}`}
              aria-label={t("categories.actions.archive", { name: label })}
              onClick={() => setArchiveTarget(node)}
              className={ICON_BUTTON_CLASS}
            >
              <Archive size={16} strokeWidth={1.75} aria-hidden />
            </button>
          )}
          <button
            type="button"
            data-testid={`admin-categories-merge-${node.id}`}
            aria-label={t("categories.merge.rowAction", { name: label })}
            onClick={() => setMergeSource(node)}
            className={ICON_BUTTON_CLASS}
          >
            <GitMerge size={16} strokeWidth={1.75} aria-hidden />
          </button>
        </div>
      </div>
    );
  };

  const isEmpty = !isLoading && !isError && displayNodes.length === 0;

  return (
    <div data-testid="admin-categories-page" className="flex flex-col gap-4">
      <AdminPageHeader
        title={t("categories.title")}
        actions={
          <Button
            variant="primary"
            size="md"
            leading="plus"
            data-testid="admin-categories-new"
            onClick={() => setDialog({ open: true, category: null, parentId: null })}
            className="min-h-[44px]"
          >
            {t("categories.new")}
          </Button>
        }
      />

      <PendingChangesBanner />

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          data-testid="admin-categories-search"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={t("categories.searchPlaceholder")}
          aria-label={t("common.search")}
          className="min-h-[44px] w-full max-w-md rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="admin-categories-expand-all"
          onClick={() => setExpandedIds(new Set(all.map((c) => c.id)))}
          className="min-h-[44px]"
        >
          {t("categories.toolbar.expandAll")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="admin-categories-collapse-all"
          onClick={() => setExpandedIds(new Set())}
          className="min-h-[44px]"
        >
          {t("categories.toolbar.collapseAll")}
        </Button>
      </div>

      <AdminStates
        isLoading={isLoading}
        isError={isError}
        isEmpty={isEmpty}
        loadingLabel={t("loading")}
        emptyLabel={t("categories.empty")}
        errorLabel={t("error.loadFailed")}
        testid="admin-categories"
      >
        <CategoryTree<CategoryNode>
          nodes={treeNodes}
          expandedIds={effectiveExpandedIds}
          onToggle={toggleExpanded}
          renderRow={renderRow}
          compare={compareSiblings}
          onReorderSiblings={(parentId, orderedIds) => void handleReorderSiblings(parentId, orderedIds)}
          dragDisabled={isSearching || reorderPending}
          getToggleLabel={(node, expanded) =>
            t(expanded ? "categories.tree.collapse" : "categories.tree.expand", {
              name: categoryLabel(node, lang),
            })
          }
          getDragHandleLabel={(node) =>
            t("categories.tree.dragHandle", { name: categoryLabel(node, lang) })
          }
          testid="admin-categories-tree"
        />
      </AdminStates>

      {isError ? (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            data-testid="admin-categories-retry"
            onClick={() => void refetch()}
            className="min-h-[44px]"
          >
            {t("categories.retry")}
          </Button>
        </div>
      ) : null}

      <CategoryFormDialog
        key={dialog.open ? `${dialog.category?.id ?? "new"}-${dialog.parentId ?? ""}` : "form-closed"}
        open={dialog.open}
        category={dialog.open ? dialog.category : null}
        parents={all}
        defaultParentId={dialog.open ? dialog.parentId : null}
        onClose={closeDialog}
        onSuccess={closeDialog}
      />

      <BottomSheet
        open={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        title={t("categories.archive.confirmTitle")}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--g-secondary)]">
            {t("categories.archive.confirmBody", { name: archiveTarget ? categoryLabel(archiveTarget, lang) : "" })}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setArchiveTarget(null)}
              disabled={archiveMutation.isPending}
              className="min-h-[44px]"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              data-testid="admin-categories-archive-confirm"
              loading={archiveMutation.isPending}
              onClick={confirmArchive}
              className="min-h-[44px]"
            >
              {t("categories.archive.confirm")}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <CategoryMergeDialog
        key={mergeSource?.id ?? "merge-closed"}
        open={mergeSource !== null}
        source={mergeSource}
        categories={all}
        onClose={() => setMergeSource(null)}
      />
    </div>
  );
}

export default CategoryListPage;
